import React, { useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, List, ListItemText, ListItemIcon,
  ListItemButton, Avatar, Chip, LinearProgress, IconButton,
} from '@mui/material';
import {
  Assignment as ChoreIcon, Today as RoutineIcon, Star as RewardIcon,
  CheckCircle as DoneIcon, RadioButtonUnchecked as UndoneIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard, SearchInput } from '../../components/common';
import { ChoreFormDialog, RoutineFormDialog, RewardFormDialog } from '../../components/dialogs';
import { useFamilyData, useFamilyMembersData, useSharedPersonData } from '../../hooks';
import { useFamily } from '../../context';
import { updateData, deletePersonData } from '../../services/dataApi';
import { colors } from '../../theme';

const ROUTINE_TYPE_COLORS: Record<string, string> = {
  morning: '#F59E0B',
  afternoon: '#F97316',
  bedtime: '#6366F1',
  custom: '#6B7280',
};

const DisciplinePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { memberList, getMemberName, getMemberColor } = useFamily();

  const [search, setSearch] = useState('');
  const [filterMemberId, setFilterMemberId] = useState('');

  const memberIds = React.useMemo(() => memberList.map((m) => m.id), [memberList]);

  // Chores and routines are person_data (assigned to people)
  const { items: chores, loading: choresLoading } = useFamilyMembersData<any>(memberIds, 'chore');
  const { items: routines, loading: routinesLoading } = useFamilyMembersData<any>(memberIds, 'routine');
  // Rewards are family_data
  const { items: rewards, loading: rewardsLoading, insert: insertReward, update: updateReward, remove: removeReward } = useFamilyData('reward');
  // Chores and routines write to person_data
  const { insertForMultiplePeople: insertChoreForPeople } = useSharedPersonData('chore');
  const { insertForMultiplePeople: insertRoutineForPeople } = useSharedPersonData('routine');

  const [choreDialogOpen, setChoreDialogOpen] = useState(false);
  const [routineDialogOpen, setRoutineDialogOpen] = useState(false);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [selectedChore, setSelectedChore] = useState<any>(null);
  const [selectedRoutine, setSelectedRoutine] = useState<any>(null);
  const [selectedReward, setSelectedReward] = useState<any>(null);

  const loading = choresLoading || routinesLoading || rewardsLoading;
  const searchLower = search.toLowerCase();

  // Filter chores
  const filteredChores = chores.filter((chore: any) => {
    const d = (chore.data?.data || {}) as any;
    if (search && !(d.name || '').toLowerCase().includes(searchLower)) return false;
    if (filterMemberId) {
      const assigned = d.assignedTo || [];
      const memberId = d.member_id;
      if (!assigned.includes(filterMemberId) && memberId !== filterMemberId) return false;
    }
    return true;
  });

  // Filter routines
  const filteredRoutines = routines.filter((routine: any) => {
    const d = (routine.data?.data || {}) as any;
    if (search && !(d.name || '').toLowerCase().includes(searchLower)) return false;
    if (filterMemberId) {
      const assigned = d.assignedTo || [];
      const memberId = d.member_id;
      if (!assigned.includes(filterMemberId) && memberId !== filterMemberId) return false;
    }
    return true;
  });

  // Toggle chore completion inline
  const handleToggleChore = async (chore: any) => {
    const d = (chore.data?.data || {}) as any;
    try {
      await updateData(chore.id, 'chore', { ...d, completed: !d.completed });
    } catch (err) {
      enqueueSnackbar('Failed to update chore', { variant: 'error' });
    }
  };

  const handleSaveChore = async (data: any) => {
    try {
      if (selectedChore?.id) { await updateData(selectedChore.id, 'chore', data); enqueueSnackbar('Chore updated', { variant: 'success' }); }
      else {
        const personIds = data.assignedTo || [];
        if (personIds.length === 0) { enqueueSnackbar('Please assign the chore to at least one person', { variant: 'warning' }); return; }
        await insertChoreForPeople(data, personIds);
        enqueueSnackbar('Chore added', { variant: 'success' });
      }
    } catch (err) { enqueueSnackbar('Failed to save chore', { variant: 'error' }); throw err; }
  };
  const handleSaveRoutine = async (data: any) => {
    try {
      if (selectedRoutine?.id) { await updateData(selectedRoutine.id, 'routine', data); enqueueSnackbar('Routine updated', { variant: 'success' }); }
      else {
        const personIds = data.assignedTo || [];
        if (personIds.length === 0) { enqueueSnackbar('Please assign the routine to at least one person', { variant: 'warning' }); return; }
        await insertRoutineForPeople(data, personIds);
        enqueueSnackbar('Routine added', { variant: 'success' });
      }
    } catch (err) { enqueueSnackbar('Failed to save routine', { variant: 'error' }); throw err; }
  };
  const handleSaveReward = async (data: any, familyIds: string[]) => {
    try {
      if (selectedReward?.id) { await updateReward(selectedReward.id, data); enqueueSnackbar('Reward updated', { variant: 'success' }); }
      else { for (const familyId of familyIds) { await insertReward(data, familyId); } enqueueSnackbar('Reward added', { variant: 'success' }); }
    } catch (err) { enqueueSnackbar('Failed to save reward', { variant: 'error' }); throw err; }
  };

  if (loading) return <LoadingSpinner message="Loading discipline data..." />;

  return (
    <Box>
      <PageHeader title="Discipline" subtitle="Manage chores, routines, and rewards" />

      {/* Search & Member Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search chores & routines..." />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip label="All" onClick={() => setFilterMemberId('')} variant={!filterMemberId ? 'filled' : 'outlined'} color={!filterMemberId ? 'primary' : 'default'} sx={{ cursor: 'pointer' }} />
          {memberList.map((member) => {
            const name = getMemberName(member.id);
            return (
              <Chip key={member.id} avatar={<Avatar sx={{ bgcolor: getMemberColor(member.id) }}>{name.charAt(0)}</Avatar>} label={name} onClick={() => setFilterMemberId(filterMemberId === member.id ? '' : member.id)} variant={filterMemberId === member.id ? 'filled' : 'outlined'} color={filterMemberId === member.id ? 'primary' : 'default'} sx={{ cursor: 'pointer' }} />
            );
          })}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard title="Chores" icon={ChoreIcon} iconColor={colors.discipline} value={chores.length} onClick={() => { setSelectedChore(null); setChoreDialogOpen(true); }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard title="Routines" icon={RoutineIcon} iconColor="#2196F3" value={routines.length} onClick={() => { setSelectedRoutine(null); setRoutineDialogOpen(true); }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard title="Rewards" icon={RewardIcon} iconColor="#FFC107" value={rewards.length} onClick={() => { setSelectedReward(null); setRewardDialogOpen(true); }} />
        </Grid>

        {/* Chores */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Chores</Typography>
              {filteredChores.length === 0 ? (
                <EmptyState icon={ChoreIcon} title="No chores" description="Create chores for family members" actionLabel="Add Chore" onAction={() => { setSelectedChore(null); setChoreDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {filteredChores.map((chore: any) => {
                    const d = (chore.data?.data || {}) as any;
                    const assignedName = d.member || d.assignedTo?.map((id: string) => getMemberName(id)).join(', ') || 'Unassigned';
                    return (
                      <ListItemButton key={chore.id} sx={{ px: 0, borderRadius: 1, opacity: d.completed ? 0.6 : 1 }} onClick={() => { setSelectedChore(chore); setChoreDialogOpen(true); }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleChore(chore); }}>
                            {d.completed ? <DoneIcon color="success" /> : <UndoneIcon />}
                          </IconButton>
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography sx={{ textDecoration: d.completed ? 'line-through' : 'none' }}>{d.name || 'Unnamed Chore'}</Typography>}
                          secondary={`${assignedName} · ${d.frequency || 'daily'}`}
                        />
                        {d.points > 0 && <Chip label={`${d.points} pts`} size="small" />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Routines */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Routines</Typography>
              {filteredRoutines.length === 0 ? (
                <EmptyState icon={RoutineIcon} title="No routines" description="Create daily routines" actionLabel="Add Routine" onAction={() => { setSelectedRoutine(null); setRoutineDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {filteredRoutines.map((routine: any) => {
                    const d = (routine.data?.data || {}) as any;
                    const tasks = d.tasks || [];
                    const completedTasks = tasks.filter((t: any) => t.completed).length;
                    const progress = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;
                    const typeColor = ROUTINE_TYPE_COLORS[d.type] || '#6B7280';
                    const assignedName = d.member || d.assignedTo?.map((id: string) => getMemberName(id)).join(', ') || '';
                    return (
                      <ListItemButton key={routine.id} sx={{ px: 0, borderRadius: 1 }} onClick={() => { setSelectedRoutine(routine); setRoutineDialogOpen(true); }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Box sx={{ width: 8, height: 32, borderRadius: 1, bgcolor: typeColor }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={d.name || 'Unnamed Routine'}
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {[d.type, d.time || d.start_time, assignedName].filter(Boolean).join(' · ')}
                              </Typography>
                              {tasks.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption">{completedTasks}/{tasks.length} tasks</Typography>
                                    <Typography variant="caption">{Math.round(progress)}%</Typography>
                                  </Box>
                                  <LinearProgress variant="determinate" value={progress} color={progress >= 100 ? 'success' : 'primary'} sx={{ mt: 0.25 }} />
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Rewards */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Rewards</Typography>
              {rewards.length === 0 ? (
                <EmptyState icon={RewardIcon} title="No rewards" description="Create rewards to motivate" actionLabel="Add Reward" onAction={() => { setSelectedReward(null); setRewardDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {rewards.map((reward) => {
                    const d = (reward.data?.data || {}) as any;
                    return (
                      <ListItemButton key={reward.id} sx={{ px: 0, borderRadius: 1 }} onClick={() => { setSelectedReward(reward); setRewardDialogOpen(true); }}>
                        <ListItemIcon><Avatar sx={{ bgcolor: '#FFC107', width: 32, height: 32 }}><RewardIcon fontSize="small" /></Avatar></ListItemIcon>
                        <ListItemText primary={d.name || 'Unnamed Reward'} secondary={`${d.pointsCost || 0} points`} />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <ChoreFormDialog open={choreDialogOpen} onClose={() => { setChoreDialogOpen(false); setSelectedChore(null); }} onSave={handleSaveChore} onDelete={selectedChore ? async () => { await deletePersonData(selectedChore.id); enqueueSnackbar('Chore deleted', { variant: 'success' }); } : undefined} chore={selectedChore} />
      <RoutineFormDialog open={routineDialogOpen} onClose={() => { setRoutineDialogOpen(false); setSelectedRoutine(null); }} onSave={handleSaveRoutine} onDelete={selectedRoutine ? async () => { await deletePersonData(selectedRoutine.id); enqueueSnackbar('Routine deleted', { variant: 'success' }); } : undefined} routine={selectedRoutine} />
      <RewardFormDialog open={rewardDialogOpen} onClose={() => { setRewardDialogOpen(false); setSelectedReward(null); }} onSave={handleSaveReward} onDelete={selectedReward ? async () => { await removeReward(selectedReward.id); enqueueSnackbar('Reward deleted', { variant: 'success' }); } : undefined} reward={selectedReward} />
    </Box>
  );
};

export default DisciplinePage;
